import { doc, type FirestoreDataConverter, getFirestore } from 'firebase/firestore'
import { z } from 'zod'

import { ErrorWithSentryCaptureContext } from '@/sentry/ErrorWithSentryCaptureContext.ts'

const SystemStatusZod = z.object({
  system: z.string(),
  status: z.enum(['ok', 'error']).or(z.string().transform(() => 'ok' as const)),
  message: z.string().optional(),
})

const ConfigurationAdminBreakersZod = z
  .object({
    regionState: z
      .object({
        state: z.enum(['ok', 'warning', 'error']).or(z.string().transform(() => 'ok' as const)),
        messageTitle: z.string().optional(),
        messageText: z.string().optional(),
        statuses: z.array(SystemStatusZod).optional().default([]),
      })
      .optional()
      .default({ state: 'ok' }),
    automatedAssignments: z
      .object({
        disabled: z.boolean(),
      })
      .optional()
      .default({ disabled: false }),
    mobileAppRequests: z
      .object({
        disabled: z.boolean(),
      })
      .optional()
      .default({ disabled: false }),
  })
  .readonly()

export type ConfigurationAdminBreakersSnapshotData = z.infer<typeof ConfigurationAdminBreakersZod>

export const DEFAULT_ADMIN_BREAKERS_VALUE = ConfigurationAdminBreakersZod.parse({})

const ConfigurationAdminBreakersConverter: FirestoreDataConverter<ConfigurationAdminBreakersSnapshotData> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot, options) => {
    const result = ConfigurationAdminBreakersZod.safeParse(snapshot.data(options))
    if (!result.success) {
      throw new ErrorWithSentryCaptureContext(
        `Invalid Configuration AdminBreakers ${snapshot.id}`,
        {
          tags: { 'firestore.zod': 'configurations' },
          fingerprint: ['firestore', 'zod', 'configurations'],
          extra: { data: snapshot.data(options), document: { path: snapshot.ref.path } },
        },
        { cause: result.error }
      )
    }
    return { _id: snapshot.id, ...result.data }
  },
}

export const adminBreakersDocument = () =>
  doc(getFirestore(), 'configurations/AdminBreakers').withConverter(ConfigurationAdminBreakersConverter)
